/**
 * PlaybookCreate — new playbook form + edit mode.
 * Two-column layout: left = name + action sequence builder; right = live summary + safety.
 * Always creates with is_active:false. Arming is deferred to PlaybookDetail.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Workflow } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CardDescription,
  Input,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  usePlaybook,
  useCreatePlaybook,
  useUpdatePlaybook,
  useChannels,
  useWatchpoints,
  type PlaybookAction,
} from '@/api/healthcheck';
import { hcExtractError } from '@/api/healthcheck';
import { ActionBuilder } from './ActionBuilder';
import { ACTION_META, type PlaybookActionType } from './action-type-meta';
import { fmtSeconds } from '../shared';
import styles from './PlaybookLayout.module.css';

interface Props {
  /** When set, component operates in edit mode (pre-loads the playbook). */
  playbookId?: string;
}

export function PlaybookCreate({ playbookId }: Props) {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const isEdit = !!playbookId;

  // In edit mode, load existing playbook
  const { data: existing, isLoading: existingLoading } = usePlaybook(playbookId);

  // ---- Form state ----------------------------------------------------------
  const [name, setName] = useState('');
  const [actions, setActions] = useState<PlaybookAction[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: channels = [] } = useChannels();
  const { data: wpData } = useWatchpoints(projectId, {});
  const watchpoints = wpData?.items ?? [];

  // Seed form from existing playbook (edit mode)
  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setActions(existing.actions ?? []);
    }
  }, [existing]);

  // ---- Mutations -----------------------------------------------------------
  const createPlaybook = useCreatePlaybook({
    onSuccess: (pb) => navigate(`${base}/playbooks/${pb.id}`),
    onError: (e) => setSubmitError(hcExtractError(e, 'Create failed')),
  });

  const updatePlaybook = useUpdatePlaybook(playbookId ?? '', {
    onSuccess: (pb) => navigate(`${base}/playbooks/${pb.id}`),
    onError: (e) => setSubmitError(hcExtractError(e, 'Update failed')),
  });

  // ---- Validation ----------------------------------------------------------
  const validate = (): boolean => {
    setShowErrors(true);
    if (!name.trim() || name.length > 255) return false;
    if (actions.length === 0) return false;
    for (const action of actions) {
      const meta = ACTION_META[action.type as PlaybookActionType];
      if (!meta) return false;
      for (const field of meta.fields) {
        if (!field.required) continue;
        const val = action.config[field.name];
        if (field.type === 'watchpoint-multiselect') {
          if (!Array.isArray(val) || val.length === 0) return false;
        } else if (field.type === 'webhook-url') {
          const urlStr = String(val ?? '').trim();
          if (!urlStr || !/^https?:\/\//i.test(urlStr)) return false;
        } else {
          if (val === undefined || val === null || String(val).trim() === '') return false;
        }
      }
      if (action.delay < 0) return false;
    }
    return true;
  };

  const handleSubmit = () => {
    setSubmitError(null);
    if (!validate()) return;

    if (isEdit) {
      updatePlaybook.mutate({ name: name.trim(), actions });
    } else {
      createPlaybook.mutate({
        project_id: projectId!,
        name: name.trim(),
        actions,
        is_active: false,
      });
    }
  };

  const pending = createPlaybook.isPending || updatePlaybook.isPending;
  const hasDestructive = actions.some(
    (a) => a.type === 'swift_blacklist' || a.type === 'alibaba_blacklist',
  );

  if (isEdit && existingLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 40, maxWidth: 400, marginBottom: 'var(--vn-space-4)' }} />
        <Skeleton style={{ height: 320 }} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Workflow size={20} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem asChild><Link to={`${base}/playbooks`}>Playbooks</Link></BreadcrumbItem>
            <BreadcrumbItem current>{isEdit ? 'Edit' : 'New playbook'}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={isEdit ? `Edit: ${existing?.name ?? '…'}` : 'New playbook'}
        description={
          isEdit
            ? 'Update name or actions. Status (armed/disarmed) is managed from the detail page.'
            : 'Build a remediation runbook. It will be created disarmed — arm it explicitly from the detail page.'
        }
      />

      <div className={styles.twoCol}>
        {/* ---- Left column: name + action sequence ------------------------ */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Name */}
          <Card flat>
            <CardHeader>
              <CardTitle>Details</CardTitle>
              <CardDescription>Give this playbook a descriptive name.</CardDescription>
            </CardHeader>
            <CardBody>
              <Field
                label="Name"
                required
                error={
                  showErrors && !name.trim()
                    ? 'Name is required.'
                    : showErrors && name.length > 255
                      ? 'Name must be 255 characters or fewer.'
                      : undefined
                }
              >
                {(p) => (
                  <Input
                    {...p}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Block L7 flood"
                    maxLength={255}
                    style={{ maxWidth: 420 }}
                    invalid={showErrors && (!name.trim() || name.length > 255)}
                  />
                )}
              </Field>
            </CardBody>
          </Card>

          {/* Action sequence builder */}
          <Card flat>
            <CardHeader>
              <CardTitle>Action sequence</CardTitle>
              <CardDescription>
                Runs top-to-bottom when the alarm fires. First action is immediate;
                subsequent actions wait their configured delay.
              </CardDescription>
            </CardHeader>
            <CardBody>
              <ActionBuilder
                actions={actions}
                channels={channels}
                watchpoints={watchpoints}
                showErrors={showErrors}
                onChange={setActions}
              />
            </CardBody>
          </Card>

          {submitError && <Alert tone="danger" title={submitError} />}

          {/* Form actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
            <Button variant="ghost" onClick={() => navigate(`${base}/playbooks`)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={pending}>
              {pending
                ? isEdit ? 'Saving…' : 'Creating…'
                : isEdit ? 'Save changes' : 'Create (disarmed)'}
            </Button>
          </div>
        </div>

        {/* ---- Right column: live summary + safety (sticky) --------------- */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', position: 'sticky', top: 'var(--vn-space-6)' }}>
          {/* Live summary */}
          <Card flat>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardBody>
              {actions.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  Add actions to see the sequence here.
                </p>
              ) : (
                <ol style={{ margin: 0, paddingLeft: 'var(--vn-space-5)', display: 'grid', gap: 'var(--vn-space-1)' }}>
                  {actions.map((a, i) => {
                    const meta = ACTION_META[a.type as PlaybookActionType];
                    return (
                      <li key={i} style={{ fontSize: 'var(--vn-font-size-sm)' }}>
                        {meta?.label ?? a.type}
                        {i > 0 && a.delay > 0 && (
                          <span style={{ color: 'var(--vn-fg-muted)' }}>
                            {' '}(wait {fmtSeconds(a.delay)} after previous)
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </CardBody>
          </Card>

          {/* Safety card */}
          <Card flat>
            <CardHeader>
              <CardTitle>Safety</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                {hasDestructive && (
                  <Alert tone="danger" title="Edge-blocking actions">
                    This playbook contains actions that block live traffic. It will be
                    created DISARMED — arm it from the detail page when you are ready.
                  </Alert>
                )}
                <Alert tone="info" title="No live simulation available">
                  There is no dry-run API yet — arming is the go-live gate. Review your
                  action config carefully before arming.
                </Alert>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </Container>
  );
}

